/**
 * O card de uma enquete — no dia do Roteiro e na tela Enquetes.
 *
 * Aberta: cada opção é um botão; tocar vota, tocar em outra muda o voto.
 * Fechada: a vencedora leva o troféu e, para quem edita o roteiro, um toque
 * a transforma em atividade do dia (a hora sugerida é a de encerrar).
 */
import { uuidv7 } from '@nv/core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePermissao, useTrip } from '../../trip/TripContext';
import { Icon } from '../../ui/icons';
import { Ilustracao } from '../../ui/Ilustracao';
import { FotoAvatar } from '../perfil/FotoAvatar';
import { usePessoas } from '../roteiro/people';
import { Card } from '../../ui/primitives';
import { mesclarDiaDoServidor } from '../roteiro/carregarDia';
import { type Enquete, enqueteApi, useInvalidarEnquetes } from './api';

export function hora(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function quando(iso: string): string {
  const d = new Date(iso);
  const hoje = new Date();
  const mesmoDia = d.getFullYear() === hoje.getFullYear() && d.getMonth() === hoje.getMonth() && d.getDate() === hoje.getDate();
  return mesmoDia ? `às ${hora(iso)}` : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} às ${hora(iso)}`;
}

export function EnqueteCard({ enquete, mostrarDia = false }: { enquete: Enquete; mostrarDia?: boolean }) {
  const { fotoDe } = usePessoas();
  const { tripId } = useTrip();
  const navigate = useNavigate();
  const podeEditarRoteiro = usePermissao('edit_itinerary');
  const avisar = useInvalidarEnquetes(tripId);
  const [mexendo, setMexendo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [horaDaAtividade, setHoraDaAtividade] = useState(hora(enquete.closesAt));

  async function agir(fn: () => Promise<unknown>) {
    setMexendo(true);
    setErro(null);
    try {
      await fn();
      avisar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não deu agora. Tente de novo.');
    } finally {
      setMexendo(false);
    }
  }

  const vencedora = enquete.winnerOptionId ? enquete.options.find((o) => o.id === enquete.winnerOptionId) : null;
  const maior = Math.max(1, ...enquete.options.map((o) => o.votes));
  const votos = (n: number) => `${n} ${n === 1 ? 'voto' : 'votos'}`;

  return (
    <Card className={enquete.isOpen ? 'enq' : 'enq enq--fechada'} data-testid="enquete">
      <div className="enc__cab">
        <Ilustracao nome="enquetes" tamanho={48} className="enq__ilustracao" />
        <span className="mapa__pessoa-texto">
          <span className="enc__titulo">{enquete.question}</span>
          <span className="mapa__pessoa-familia">
            {mostrarDia && <>{enquete.dayDate.slice(8, 10)}/{enquete.dayDate.slice(5, 7)} · </>}
            por {enquete.createdBy.name} · {enquete.isOpen ? `fecha ${quando(enquete.closesAt)}` : `encerrada · ${votos(enquete.totalVotes)}`}
          </span>
        </span>
      </div>

      <div className="enq__opcoes" role={enquete.isOpen ? 'radiogroup' : 'list'} aria-label="Opções">
        {enquete.options.map((o) => {
          const minha = o.id === enquete.myOptionId;
          const ganhou = o.id === enquete.winnerOptionId;
          const classe = ['enq__opcao', minha ? 'enq__opcao--minha' : '', ganhou ? 'enq__opcao--vencedora' : ''].filter(Boolean).join(' ');
          const conteudo = (
            <>
              <span className="enq__barra" style={{ width: `${Math.round((o.votes / maior) * 100)}%` }} aria-hidden="true" />
              {enquete.isOpen ? <span className="enq__radio" aria-hidden="true" /> : ganhou && <Icon name="trofeu" size={18} />}
              <span className="enq__opcao-texto">
                <span className="enq__opcao-nome">{o.text}</span>
                {o.placeAddress && <span className="enq__opcao-endereco">{o.placeAddress}</span>}
              </span>
              <span className="enq__opcao-votos">
                {o.voters.length > 0 && (
                  <span className="nv-avatar-stack">
                    {o.voters.slice(0, 4).map((v) => (
                      <FotoAvatar key={v.memberId} nome={v.name} url={fotoDe(v.memberId).url} cor={fotoDe(v.memberId).cor} tamanho={20} className="bag__mini-avatar" />
                    ))}
                  </span>
                )}
                <span className="mono">{o.votes}</span>
              </span>
            </>
          );
          return enquete.isOpen ? (
            <button key={o.id} type="button" role="radio" aria-checked={minha} className={classe} disabled={mexendo} onClick={() => void agir(() => enqueteApi.votar(tripId, enquete.id, o.id))}>
              {conteudo}
            </button>
          ) : (
            <div key={o.id} role="listitem" className={classe} aria-label={`${o.text}: ${votos(o.votes)}${ganhou ? ' · vencedora' : ''}`}>
              {conteudo}
            </div>
          );
        })}
      </div>

      {enquete.isOpen && (
        <p className="enq__rodape">
          {enquete.myOptionId ? 'Seu voto está registrado — toque em outra para mudar.' : 'Toque numa opção para votar.'} {votos(enquete.totalVotes)}.
        </p>
      )}

      {!enquete.isOpen && !vencedora && <p className="enq__rodape">Ninguém votou.</p>}

      {!enquete.isOpen && vencedora && enquete.activityId && (
        <button type="button" className="nv-btn nv-btn--secondary nv-btn--block enq__ver" onClick={() => navigate(`/roteiro/${enquete.dayDate}/${enquete.activityId}`)}>
          <Icon name="roteiro" size={16} /> Ver no roteiro: {vencedora.text}
        </button>
      )}

      {!enquete.isOpen && vencedora && !enquete.activityId && podeEditarRoteiro && (
        <div className="enq__virar">
          <label className="field enq__hora">
            <span className="field__label">Hora</span>
            <input className="field__input" type="time" value={horaDaAtividade} onChange={(e) => setHoraDaAtividade(e.target.value)} aria-label="Hora da atividade" />
          </label>
          <button
            type="button"
            className="nv-btn nv-btn--primary"
            disabled={mexendo || !/^([01]\d|2[0-3]):[0-5]\d$/.test(horaDaAtividade)}
            onClick={() =>
              void agir(async () => {
                await enqueteApi.virarAtividade(tripId, enquete.id, { id: uuidv7(), startTime: horaDaAtividade, idempotencyKey: uuidv7() });
                await mesclarDiaDoServidor(enquete.dayId).catch(() => undefined);
              })
            }
          >
            <Icon name="criar" size={16} /> Virar atividade
          </button>
        </div>
      )}

      {erro && <p className="auth-error" role="alert">{erro}</p>}

      {enquete.canClose && (
        <button type="button" className="nv-btn nv-btn--ghost nv-btn--block" disabled={mexendo} onClick={() => void agir(() => enqueteApi.encerrar(tripId, enquete.id))}>
          Encerrar agora
        </button>
      )}
    </Card>
  );
}
